/**
 * Storezone — Select
 */

import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react'
import './Select.css'

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  helperText?: string
  error?: string
  fullWidth?: boolean
  placeholder?: string
  options?: { value: string; label: string; disabled?: boolean }[]
  children?: ReactNode
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      helperText,
      error,
      fullWidth = false,
      placeholder,
      options,
      children,
      id,
      className = '',
      ...props
    },
    ref,
  ) => {
    const selectId = id ?? `sz-select-${Math.random().toString(36).slice(2, 9)}`
    const errorId = `${selectId}-error`

    return (
      <div className={`sz-select-root ${fullWidth ? 'sz-select-root--full' : ''} ${className}`}>
        {label && (
          <label htmlFor={selectId} className="sz-select-label">
            {label}
          </label>
        )}
        <div className={`sz-select-wrap ${error ? 'sz-select-wrap--error' : ''}`}>
          <select
            ref={ref}
            id={selectId}
            className="sz-select"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options
              ? options.map((o) => (
                  <option key={o.value} value={o.value} disabled={o.disabled}>
                    {o.label}
                  </option>
                ))
              : children}
          </select>
          <span className="sz-select-arrow" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>
        {error && (
          <p id={errorId} className="sz-select-error" role="alert">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p className="sz-select-helper">{helperText}</p>
        )}
      </div>
    )
  },
)
Select.displayName = 'Select'
